import React, { Component } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import * as Formik from "formik";
import * as Yup from "yup";
import Velocity from "velocity-animate";
import DateTimeValidation from "./Validation/DateTime";
import MixedValidation from "./Validation/MixedValidation";
import StringValidation from "./Validation/StringValidation";
import Part from "./Part";
import VisibilityConditionHelper from "./VisibilityCondition";
import { getDaysInMonth, format } from "date-fns";

Yup.datetime = DateTimeValidation;
Yup.addMethod(Yup.mixed, "equalsField", MixedValidation.equalsField);
Yup.addMethod(Yup.mixed, "notEqualsField", MixedValidation.notEqualsField);
Yup.addMethod(Yup.string, "ovchipkaart", StringValidation.ovchipkaart);
Yup.addMethod(Yup.string, "postalcode", StringValidation.postalcode);
Yup.addMethod(Yup.string, "maxsize", StringValidation.maxSize);
Yup.addMethod(Yup.string, "max", StringValidation.maxLength);
Yup.addMethod(Yup.string, "commaDecimal", StringValidation.commaDecimal);
Yup.addMethod(Yup.string, "hiddenChars", StringValidation.hiddenChars);
Yup.addMethod(Yup.string, "normalChars", StringValidation.normalChars);
Yup.addMethod(
  Yup.string,
  "validDiscountCode",
  StringValidation.validDiscountCode
);
Yup.addMethod(
  Yup.string,
  "validNumberOfFreeEtickets",
  StringValidation.validNumberOfFreeEtickets
);

class Form extends Component {
  static propTypes = {
    config: PropTypes.object.isRequired,
    onSubmit: PropTypes.func,
    onSubmitted: PropTypes.func,
    onError: PropTypes.func,
    onChange: PropTypes.func,
    templates: PropTypes.object.isRequired,
    resetAfterSubmitted: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.errorRef = React.createRef();
    this.formikRef = React.createRef();
  }

  componentDidMount() {
    if (!this.props.config.submit) return;

    const formik = this.formikRef.current;
    window.addEventListener("pagehide", (e) => {
      if (e.persisted) {
        formik.setSubmitting(false);
        document.activeElement.blur();
      }
    });
    if (this.props.config.submit.immediately) {
      formik.submitForm();
      return;
    }

    if (this.props.config.submit.immediatelyIfPrefilled) {
      const fields = Form.flattenFields(
        this.props.config.steps[0],
        false
      ).filter((f) => f.type == "hidden" || f.validation);
      const queryStringLength = window.location.search.length
        ? window.location.search.split("&").length
        : 0;
      const fieldsLength = Object.keys(fields).length;
      if (queryStringLength >= fieldsLength) formik.submitForm();
      return;
    }

    const persist = this.props.config.persist;
    if (persist) {
      const dataString = sessionStorage.getItem(persist);
      if (dataString) {
        const data = JSON.parse(dataString);
        formik.setValues(data, true);
        Object.entries(data).forEach(([key, value]) =>
          this.fieldChanged(key, value)
        );
      }
    }
  }

  configToInitialValues(config) {
    let step = config.steps[0]; //Todo: Add multistep support
    const fields = Form.flattenFields(step, true);

    const values = [];
    fields.forEach((part) => {
      if (part.part !== "field") return;

      let value = part.value !== undefined ? part.value : "";
      if (config.syncQueryString) {
        let queryStringValue = new URLSearchParams(window.location.search).get(
          part.name
        );
        try {
          queryStringValue = JSON.parse(queryStringValue);
        } catch (ex) {}
        value = queryStringValue !== null ? queryStringValue : value;
      }

      values[part.name] = value;
    });

    return values;
  }

  static flattenFields = (group, decodeDots = false) => {
    const parts = group.parts
      .reduce((flat, part) => {
        if (group.visibility) {
          //Todo: Merge visiblity condition of the group into the visibility property of the part/field
          part.visibility = group.visibility;
        }
        return flat.concat(
          part.part === "group" ? Form.flattenFields(part, decodeDots) : part
        );
      }, [])
      .filter((part) => part.part !== "content");

    if (decodeDots) {
      parts.forEach((part) => {
        if (part.name) part.name = Form.encodeDots(part.name);
      });
    }

    return parts;
  };

  configToValidationShape(config) {
    let step = config.steps[0]; //Todo: Add multistep support
    const fields = Form.flattenFields(step, true);

    let shape = {};
    fields.forEach((part) => {
      if (!part.name || !part.validation || !part.validation.rules) return;

      const typeRule =
        part.validation.rules.find((rule) => rule.type === "type") || {};
      const typeErrorMessage =
        typeRule.error || `You must specify a ${part.validation.type}`;
      try {
        shape[part.name] =
          Yup[part.validation.type]().typeError(typeErrorMessage);
      } catch (error) {
        return console.error(
          `Validation type "${part.validation.type}" is invalid in field "${part.name}". Possible validation types can be found here: https://github.com/jquense/yup#yup`
        );
      }
      part.validation.rules
        .filter((rule) => rule !== typeRule)
        .forEach((rule) => {
          let params = rule.params || [];
          const ruleFunction = shape[part.name][rule.type];
          if (!ruleFunction)
            return console.error(
              `Validation rule "${rule.type}" for type "${part.validation.type}" is invalid in field "${part.name}". Possible validation rules can be found here: https://github.com/jquense/yup#yup`
            );

          try {
            //JSON cannot contain regular expressions so we need to convert strings manually to a RegExp object
            if (rule.type === "matches")
              params = params.map(
                (param, index) =>
                  new RegExp(
                    param,
                    rule.flags && rule.flags[index] ? rule.flags : "i"
                  )
              );

            shape[part.name] = shape[part.name][rule.type](
              ...params,
              rule.error
            );
          } catch (error) {
            return console.error(
              `Validation parameters for rule "${rule.type}" are invalid in field "${part.name}".\n`,
              error
            );
          }
        });
    });
    return shape;
  }

  validationShapeToSchema(validationShape) {
    return Yup.object().shape(validationShape);
  }

  static decodeDots = (name) => {
    return name.replace(/%2E/g, ".");
  };
  static encodeDots = (name) => {
    return name.replace(/\./g, "%2E");
  };

  async validate(config, validationShape, values) {
    let step = config.steps[0]; //Todo: Add multistep support
    const fields = Form.flattenFields(step, true);
    const visibleFieldsName = fields
      .filter((field) => VisibilityConditionHelper.isVisible(field, values))
      .map((field) => field.name);

    const visibleFieldsValidationShape = {};
    Object.keys(validationShape).forEach((fieldName) => {
      if (visibleFieldsName.indexOf(fieldName) !== -1) {
        visibleFieldsValidationShape[fieldName] = validationShape[fieldName];
      }
    });

    const validationSchema = this.validationShapeToSchema(
      visibleFieldsValidationShape
    );
    try {
      await Formik.validateYupSchema(values, validationSchema);
    } catch (error) {
      throw Formik.yupToFormErrors(error);
    }
  }

  render() {
    const { config, templates } = this.props;
    const { steps } = config;
    const Step = templates.Step;
    const initialValues = this.configToInitialValues(config);
    const validationShape = this.configToValidationShape(config);
    const Wrapper = templates.Form.Wrapper;

    return (
      <Formik.Formik
        ref={this.formikRef}
        initialValues={initialValues}
        validateOnBlur={config.validateOnBlur}
        validateOnChange={config.validateOnChange}
        validate={async (values) =>
          await this.validate(config, validationShape, values)
        }
        onSubmit={(values, formik, config) => this.handleSubmit(values, config)}
        render={(formik) => {
          this.handleFormikRender(formik);

          return (
            <Wrapper config={config}>
              <Formik.Form
                onChange={(e) => {
                  if (e.target["type"] == "radio") {
                    this.fieldChanged(
                      e.target.name,
                      e.target.getAttribute("aria-label")
                    );
                  } else this.fieldChanged(e.target.name, e.target.value);
                }}
              >
                {steps.map(
                  (
                    step,
                    i //Todo: Add multistep support
                  ) => (
                    <Step
                      key={i}
                      config={step}
                      formik={formik}
                      form={this}
                      errorMessages={this.renderErrorMessages(formik, step)}
                      submitButton={this.renderSubmitButton(formik)}
                      parts={step.parts.map((part, i) => (
                        <Part
                          key={i}
                          templates={templates}
                          config={part}
                          step={step}
                          formik={formik}
                          form={this}
                        />
                      ))}
                    />
                  )
                )}
              </Formik.Form>
            </Wrapper>
          );
        }}
      />
    );
  }

  fieldChanged(name, value) {
    if (this.props.onChange) this.props.onChange(name, value);
  }

  renderSubmitButton(formik) {
    const { config, templates } = this.props;
    if (config.submit.hideButton) return null;
    const Submit = templates.Form.Submit;

    return (
      <Submit
        disabled={config.submit.disabled}
        config={config.submit}
        isSubmitting={formik.isSubmitting}
        handleClick={(e) => this.handleSubmitButtonClick(e)}
      />
    );
  }

  renderErrorMessages(formik) {
    const { config, templates } = this.props;
    const { submit, validation, errorMessage, steps } = config;
    const step = steps[0]; //Todo: Multistep support
    const fields = Form.flattenFields(step, false);

    const fieldErrors = Object.keys(formik.errors).map((name) => ({
      field: fields.find((field) => field.name === name),
      errorMessage: formik.errors[name],
    }));
    const Errors = templates.Form.Errors;

    if (formik.submitCount === 0 && errorMessage)
      return (
        <Errors
          ref={this.errorRef}
          submitError={true}
          submitErrorMessage={errorMessage}
        />
      );

    if (
      formik.submitCount === 0 ||
      !Object.keys(formik.errors).length ||
      !validation.errorMessage
    )
      return null;

    return (
      <Errors
        ref={this.errorRef}
        submitError={formik.errors.submit}
        submitErrorMessage={submit.errorMessage}
        fieldErrors={fieldErrors}
        fieldErrorsMessage={validation.errorMessage}
      />
    );
  }

  handleFormikRender(formik) {
    try {
      if (
        !this.props.config.submit ||
        !this.props.config.submit.immediatelyOnChange
      )
        return;

      if (JSON.stringify(formik.values) === this.previousValues) return;
      this.previousValues = JSON.stringify(formik.values);

      //Delay so that the new form can be rendered before the submit
      setTimeout(() => this.handleSubmit(formik.values), 1);
    } catch (ex) {
      console.error(ex);
    }
  }

  handleSubmitButtonClick(e) {
    setTimeout(() => {
      //Delay the submission to make sure the field values are set and formik fires the event
      const formik = this.formikRef.current;
      formik.submitForm();

      setTimeout(() => {
        this.scrollToError(formik);
      }, 1);
    }, 0);
  }

  async handleSubmit(values, config) {
    if (this.props.config.syncQueryString) {
      var searchParams = new URLSearchParams(window.location.search);
      Object.keys(values).forEach((name) => {
        let value = values[name];
        try {
          value = typeof value === "object" ? JSON.stringify(value) : value;
        } catch (ex) {}
        searchParams.set(name, value);
      });
      const url = `${window.location.pathname}?${searchParams.toString()}${
        window.location.hash
      }`;
      window.history.replaceState(null, "", url);
    }

    const formik = this.formikRef.current;

    const persist = this.props.config.persist;
    if (persist) {
      sessionStorage.setItem(persist, JSON.stringify(values));
    }

    try {
      values = Object.assign({}, values);

      if (this.props.onSubmit) {
        this.props.onSubmit(values, this.props.config);
      }

      const { config, axios, isTravelPlanner } = this.props;
      const response = await Form.executeRequest(config, axios, values, this);
      var prevFormTime = null;
      var nextFormTime = null;
      if (isTravelPlanner) {
        if (response.data.result.planner === "otp2") {
          prevFormTime = new Date(
            response.data.result.result.metadata.prevDateTime
          );
          nextFormTime = new Date(
            response.data.result.result.metadata.nextDateTime
          );

          var currentDay = parseInt(getDaysInMonth(values["date"]));

          var allItineraries = response.data.result.result.plan.itineraries;
          var nextValues = values;

          while (
            nextFormTime.getDate() === currentDay &&
            allItineraries.length <= 6
          ) {
            var nextTimeString = format(nextFormTime, "HH:mm");
            nextValues["time"] = nextTimeString;

            const nextResponse = await Form.executeRequest(
              config,
              axios,
              nextValues,
              this
            );

            nextFormTime =
              nextResponse.data.result.result.metadata.nextDateTime;

            allItineraries = [
              ...allItineraries,
              ...nextResponse.data.result.result.plan.itineraries,
            ];
          }
          response.data.result.result.plan.itineraries = allItineraries;
        }
      }
      if (this.props.onSubmitted)
        await this.props.onSubmitted(
          response,
          values,
          prevFormTime,
          nextFormTime
        );

      if (this.props.resetAfterSubmitted) {
        formik.resetForm(this.configToInitialValues(this.props.config));
      }
      formik.setStatus({ success: true });
    } catch (error) {
      console.error(error);
      formik.setStatus({ success: false });
      formik.setErrors({ submit: error.message });
      this.scrollToError(formik);

      if (this.props.onError) this.props.onError(error);
    }
    formik.setSubmitting(false);
  }

  static decodeDotsInObjectProperties = (values) => {
    let tempValues = values;
    values = {};
    Object.keys(tempValues).forEach((name) => {
      values[Form.decodeDots(name)] = tempValues[name];
    });
    return values;
  };

  static executeRequest = (
    config,
    axios,
    values,
    formComponent = undefined
  ) => {
    if (!axios) return null;

    const params = {
      action: config.submit.action || "/",
      method: config.submit.method || "POST",
      async: config.submit.async || false,
    };

    return new Promise((resolve, reject) => {
      if (params.async) {
        const options = {
          method: params.method,
          url: params.action,
        };

        values = Form.decodeDotsInObjectProperties(values);

        if (params.method.toUpperCase() === "POST") {
          const data = new FormData();
          Object.keys(values).forEach((name) => {
            data.append(name, values[name]);
          });
          options.data = data;
          options.config = {
            headers: { "Content-Type": "multipart/form-data" },
          };
        } else if (params.method.toUpperCase() === "GET") {
          options.params = values;
        }

        axios(options)
          .then((response) => {
            resolve(response);
          })
          .catch((response) => {
            reject(response);
          });
      } else {
        if (!formComponent)
          return console.error(
            "Cannot submit a form synchronously without a Form component."
          );

        const form = ReactDOM.findDOMNode(formComponent).querySelector("form");
        form.setAttribute("method", params.method);
        form.setAttribute("action", params.action);
        if (params.method.toUpperCase() === "POST") {
          form.setAttribute("enctype", "multipart/form-data");
        }

        const fields = form.querySelectorAll("[name]");
        [...fields].forEach((field) => {
          field.setAttribute("name", Form.decodeDots(field.name));
        });

        form.submit();
      }
    });
  };

  getScrollTopOffset() {
    let elemOffset = 0;
    [...document.querySelectorAll("*")]
      .filter((elem) => {
        if (!elem) return false;
        const style = getComputedStyle(elem);
        if (
          style.position !== "fixed" ||
          style.top !== "0px" ||
          style.visibility === "hidden" ||
          style.pointerEvents === "none"
        )
          return false;
        return true;
      })
      .find((elem) => {
        elemOffset -= elem.offsetHeight;
        return true;
      });

    return elemOffset;
  }

  scrollToError(formik) {
    if (!this.errorRef.current) return;
    const message = ReactDOM.findDOMNode(this.errorRef.current);
    const rect = message.getBoundingClientRect();
    if (rect.top >= 0 && rect.bottom <= window.innerHeight) return;

    let offset = -15;
    offset += this.getScrollTopOffset();

    Velocity(message, "scroll", {
      axis: "y",
      offset,
      duration: 250,
      easing: "ease-in-out",
      complete: () => {
        //var errorNames = formik//.filter(name => name !== 'submit')
        //console.log(errorNames)
        //document.querySelector(`[name=${errorNames[0]}]`).focus()
      },
    });
  }
}

export default Form;



// WEBPACK FOOTER //
// ./src/components/Form/Form.js